import { Outlet, createRootRoute, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { featureRoutes } from "./features";
import { deploymentRoutes } from "@backstory/deployment";
import { RequireAuth } from "./auth/RequireAuth";
import { Shell } from "./components/Shell";
import { SessionsPage } from "./routes/SessionsPage";
import { SessionPage } from "./routes/SessionPage";
import { VisitPage } from "./routes/VisitPage";
import { LegacyShareRedirect, SharePage } from "./routes/SharePage";
import { LoginPage } from "./routes/auth/LoginPage";
import { SignupPage } from "./routes/auth/SignupPage";
import { ForgotPage, InvitePage, MagicLinkPage, MfaPage, ResetPage, VerifyEmailPage } from "./routes/auth/MiscAuthPages";
import { ProfilePage } from "./routes/settings/ProfilePage";
import { OrgSettingsPage } from "./routes/org/OrgSettingsPage";
import { FeaturesPage } from "./routes/org/FeaturesPage";
import { MembersPage } from "./routes/org/MembersPage";
import { SdkKeysPage } from "./routes/org/SdkKeysPage";
import { PrivacyPreviewPage } from "./routes/org/PrivacyPreviewPage";
import { TokensPage } from "./routes/org/TokensPage";
import { AuditPage } from "./routes/org/AuditPage";
import { SsoPage } from "./routes/org/SsoPage";
import { NetworksPage } from "./routes/org/NetworksPage";
import { HipaaPage } from "./routes/org/HipaaPage";

const rootRoute = createRootRoute({ component: () => <Outlet /> });

// Public routes (no shell).
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/login", component: LoginPage, validateSearch: (s: Record<string, unknown>) => s as { next?: string; msg?: string; error?: string } });
const signupRoute = createRoute({ getParentRoute: () => rootRoute, path: "/signup", component: SignupPage, validateSearch: (s: Record<string, unknown>) => s as { invite?: string; plan?: "free" | "team" | "business"; token?: string; email?: string } });
const verifyRoute = createRoute({ getParentRoute: () => rootRoute, path: "/verify-email", component: VerifyEmailPage, validateSearch: (s: Record<string, unknown>) => s as { token?: string } });
const magicRoute = createRoute({ getParentRoute: () => rootRoute, path: "/magic-link", component: MagicLinkPage, validateSearch: (s: Record<string, unknown>) => s as { token?: string; next?: string } });
const forgotRoute = createRoute({ getParentRoute: () => rootRoute, path: "/forgot", component: ForgotPage });
const resetRoute = createRoute({ getParentRoute: () => rootRoute, path: "/reset", component: ResetPage, validateSearch: (s: Record<string, unknown>) => s as { token?: string } });
const mfaRoute = createRoute({ getParentRoute: () => rootRoute, path: "/mfa", component: MfaPage, validateSearch: (s: Record<string, unknown>) => s as { challengeId?: string; methods?: string; next?: string } });
const inviteRoute = createRoute({ getParentRoute: () => rootRoute, path: "/invite/$token", component: InvitePage });
const legacyShareRoute = createRoute({ getParentRoute: () => rootRoute, path: "/share/$token", component: LegacyShareRedirect, validateSearch: (s: Record<string, unknown>) => s as { t?: number | string } });
const sharedRoute = createRoute({ getParentRoute: () => rootRoute, path: "/shared/$token", component: SharePage, validateSearch: (s: Record<string, unknown>) => s as { t?: number | string } });

// Authenticated app.
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: () => (
    <RequireAuth>
      <Shell />
    </RequireAuth>
  ),
});
const sessionsRoute = createRoute({ getParentRoute: () => appRoute, path: "/", component: SessionsPage, validateSearch: (s: Record<string, unknown>) => s });
const sessionRoute = createRoute({ getParentRoute: () => appRoute, path: "/sessions/$id", component: SessionPage, validateSearch: (s: Record<string, unknown>) => s as { t?: number | string; at?: number | string } });
const visitRoute = createRoute({ getParentRoute: () => appRoute, path: "/visits/$id", component: VisitPage });
const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/profile",
  component: ProfilePage,
  validateSearch: (s: Record<string, unknown>) => {
    const tab = s.tab === "password" || s.tab === "two-factor" || s.tab === "sessions" ? s.tab : undefined;
    return { tab, setupMfa: typeof s.setupMfa === "string" ? s.setupMfa : undefined } as { tab?: "password" | "two-factor" | "sessions"; setupMfa?: string };
  },
});
const orgSettingsRoute = createRoute({ getParentRoute: () => appRoute, path: "/orgs/$org/settings", component: OrgSettingsPage });
const featuresRoute = createRoute({ getParentRoute: () => appRoute, path: "/orgs/$org/features", component: FeaturesPage });
const membersRoute = createRoute({ getParentRoute: () => appRoute, path: "/orgs/$org/members", component: MembersPage });
const keysRoute = createRoute({ getParentRoute: () => appRoute, path: "/orgs/$org/keys", component: SdkKeysPage });
const privacyPreviewRoute = createRoute({ getParentRoute: () => appRoute, path: "/orgs/$org/privacy-preview", component: PrivacyPreviewPage });
const tokensRoute = createRoute({ getParentRoute: () => appRoute, path: "/orgs/$org/tokens", component: TokensPage });
const auditRoute = createRoute({ getParentRoute: () => appRoute, path: "/orgs/$org/audit", component: AuditPage });
const ssoRoute = createRoute({ getParentRoute: () => appRoute, path: "/orgs/$org/sso", component: SsoPage });
const networksRoute = createRoute({ getParentRoute: () => appRoute, path: "/orgs/$org/networks", component: NetworksPage });
const hipaaRoute = createRoute({ getParentRoute: () => appRoute, path: "/orgs/$org/hipaa", component: HipaaPage });
const usageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orgs/$org/usage",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/orgs/$org/billing", params });
  },
});
const modeRoutes = deploymentRoutes(rootRoute, appRoute);

export const routeTree = rootRoute.addChildren([
  loginRoute,
  signupRoute,
  verifyRoute,
  magicRoute,
  forgotRoute,
  resetRoute,
  mfaRoute,
  inviteRoute,
  legacyShareRoute,
  sharedRoute,
  appRoute.addChildren([
    sessionsRoute,
    sessionRoute,
    visitRoute,
    profileRoute,
    orgSettingsRoute,
    featuresRoute,
    membersRoute,
    keysRoute,
    privacyPreviewRoute,
    tokensRoute,
    auditRoute,
    ssoRoute,
    networksRoute,
    hipaaRoute,
    usageRoute,
    ...modeRoutes.app,
    ...featureRoutes(appRoute),
  ]),
  ...modeRoutes.root,
]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
